/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Button,
  Card,
  Checkbox,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  PhoneInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@racefront/shadcn-ui'
import dayjs from 'dayjs'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import * as z from 'zod'
import { SignUpData } from '../../../types/user'
import { DateInput } from '../user-form/date-input'
import { useSignUpSchema } from '../user-form/schema'
import { useAuthStore } from '../../data/stores/auth.store'
import { TERMS_URL } from '../../constants'
import { useLocalization } from '@racefront/common/context/localization-context'

type SignUpFormProps = {
  isLoading?: boolean
  onSubmit?: (data: SignUpData) => void
  initialRole?: 'organizer' | 'profile_user'
}

export function SignUpForm({ onSubmit, isLoading, initialRole }: SignUpFormProps) {
  const intl = useIntl()
  const { lang } = useLocalization()
  const signUpSchema = useSignUpSchema()
  const { context } = useAuthStore()
  const [defaultCountry, setDefaultCountry] = useState<string>()

  type FormSignUp = z.infer<typeof signUpSchema>

  const form = useForm<FormSignUp>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      locale: intl.locale,
      role: initialRole || 'organizer',
      email: context?.username,
    } as any,
  })

  // Detect country from browser locale
  useEffect(() => {
    try {
      // Get the country code from the browser's locale (e.g., 'en-US' -> 'US')
      const locale = navigator.language || intl.locale
      const countryCode = locale.split('-')[1]?.toUpperCase()

      if (countryCode && countryCode.length === 2) {
        setDefaultCountry(countryCode)
      }
    } catch (error) {
      console.error('Error detecting country from locale:', error)
    }
  }, [intl.locale])

  const handleSubmit = (data: FormSignUp) => {
    onSubmit?.(data as any)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <FormattedMessage id="form.email_label" defaultMessage="Email" />
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="email"
                  className="notranslate"
                  translate="no"
                  placeholder={intl.formatMessage({ id: 'form.email_placeholder', defaultMessage: 'your@email.com' })}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phoneNumber"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>
                <FormattedMessage id="form.phoneNumber_label" defaultMessage="Phone Number" />
              </FormLabel>
              <FormControl>
                <PhoneInput
                  {...field}
                  defaultCountry={defaultCountry as any}
                  className={`notranslate ${fieldState.error ? '[&_button]:border-destructive' : ''}`}
                  translate="no"
                  placeholder={intl.formatMessage({
                    id: 'form.phoneNumber_placeholder',
                    defaultMessage: 'Enter your phone number',
                  })}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField
            control={form.control}
            name="firstname"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <FormattedMessage id="form.firstname_label" defaultMessage="First Name" />
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="notranslate"
                    translate="no"
                    placeholder={intl.formatMessage({ id: 'form.firstname_placeholder', defaultMessage: 'John' })}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastname"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <FormattedMessage id="form.lastname_label" defaultMessage="Last Name" />
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="notranslate"
                    translate="no"
                    placeholder={intl.formatMessage({ id: 'form.lastname_placeholder', defaultMessage: 'Doe' })}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="birthdate"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>
                <FormattedMessage id="form.birthdate_label" defaultMessage="Birth Date" />
              </FormLabel>
              <DateInput
                field={field as any}
                hasError={!!fieldState.error}
                isRequired
                formater={(date) => dayjs(date)?.format('YYYY-MM-DD')}
                placeholder={<FormattedMessage id="form.birthdate_placeholder" defaultMessage="Pick a date" />}
                lang={lang}
              />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="gender"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>
                <FormattedMessage id="form.gender_label" defaultMessage="Gender" />
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger
                    className={`notranslate ${fieldState.error ? 'border-destructive' : ''}`}
                    translate="no"
                  >
                    <SelectValue
                      placeholder={intl.formatMessage({
                        id: 'form.gender_placeholder',
                        defaultMessage: 'Select gender',
                      })}
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="1">
                    <FormattedMessage id="form.gender.male" defaultMessage="Male" />
                  </SelectItem>
                  <SelectItem value="2">
                    <FormattedMessage id="form.gender.female" defaultMessage="Female" />
                  </SelectItem>
                  <SelectItem value="3">
                    <FormattedMessage id="form.gender.not_applicable" defaultMessage="Other" />
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {form.watch('role') === 'organizer' && (
          <div className="space-y-4">
            <Card className="mb-4">
              <FormField
                control={form.control}
                name="cooperationAgreement"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="notranslate"
                        translate="no"
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        <FormattedMessage
                          id="Auth.SignUp.AcceptCooperation"
                          defaultMessage="I accept Cooperation Agreement (T&C)"
                        />
                      </FormLabel>
                      <FormDescription>
                        <FormattedMessage
                          id="Auth.SignUp.CooperationDescriptionHTML"
                          defaultMessage="You agree to our {link}"
                          values={{
                            link: (
                              <Link
                                className="text-primary underline"
                                to={TERMS_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <FormattedMessage
                                  id="Auth.SignUp.CooperationLink"
                                  defaultMessage="Cooperation Agreement ↗"
                                />
                              </Link>
                            ),
                          }}
                        />
                      </FormDescription>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
            </Card>

            <Card className="mb-4">
              <FormField
                control={form.control}
                name="dataProcessingAgreement"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="notranslate"
                        translate="no"
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        <FormattedMessage
                          id="Auth.SignUp.AcceptDataProcessing"
                          defaultMessage="Accept Data Processing Agreement"
                        />
                      </FormLabel>
                      <FormDescription>
                        <FormattedMessage
                          id="Auth.SignUp.DataProcessingDescriptionHTML"
                          defaultMessage="You agree to our {link}"
                          values={{
                            link: (
                              <Link
                                className="text-primary underline"
                                to={TERMS_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <FormattedMessage
                                  id="Auth.SignUp.DataProcessingLink"
                                  defaultMessage="Data Processing Agreement ↗"
                                />
                              </Link>
                            ),
                          }}
                        />
                      </FormDescription>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
            </Card>
          </div>
        )}

        <Button className="flex w-full gap-2" type="submit" disabled={isLoading}>
          {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          <FormattedMessage id="common.submit" defaultMessage="Submit" />
        </Button>

        <p className="text-muted-foreground text-center text-sm">
          <FormattedMessage
            id="common.termsConditionsNotice"
            defaultMessage="By submitting, you agree to our <a>Terms & Conditions</a>."
            values={{
              a: (chunks) => (
                <a
                  href={TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-foreground underline"
                >
                  {chunks}
                </a>
              ),
            }}
          />
        </p>
      </form>
    </Form>
  )
}
